import { useState } from 'react';
import { categories, siteInfo } from '@/mocks/categories';

interface ArticleSection {
  heading: string;
  content: string;
  isFAQ?: boolean;
  faqs?: { q: string; a: string }[];
}

interface ArticleData {
  title: string;
  slug: string;
  metaDescription: string;
  publishedDate: string;
  modifiedDate?: string;
  author: string;
  sections: ArticleSection[];
}

interface ArticleLayoutProps {
  article: ArticleData;
  heroImage: string;
  heroImageAlt: string;
  heroImageTitle: string;
  tag: string;
  relatedArticles: { title: string; slug: string }[];
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hipobuyspread.rest';

function buildArticleJsonLd(article: ArticleData, heroImage: string, heroImageAlt: string) {
  const pageUrl = `${SITE_URL}/${article.slug}`;
  const dateModified = article.modifiedDate || article.publishedDate;

  const blogPostingLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "description": article.metaDescription,
    "image": heroImage,
    "datePublished": article.publishedDate,
    "dateModified": dateModified,
    "author": {
      "@type": "Organization",
      "name": article.author,
      "url": SITE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": "Hipobuy Spreadsheet Hub",
      "url": SITE_URL
    },
    "url": pageUrl,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": pageUrl
    }
  };

  const faqSection = article.sections.find(s => s.isFAQ && s.faqs && s.faqs.length > 0);
  const faqPageLd = faqSection ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqSection.faqs!.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  } : null;

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": SITE_URL
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": article.title,
        "item": pageUrl
      }
    ]
  };

  return { blogPostingLd, faqPageLd, breadcrumbLd };
}

export default function ArticleLayout({ article, heroImage, heroImageAlt, heroImageTitle, tag, relatedArticles }: ArticleLayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { blogPostingLd, faqPageLd, breadcrumbLd } = buildArticleJsonLd(article, heroImage, heroImageAlt);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogPostingLd) }}
      />
      {faqPageLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPageLd) }}
        />
      )}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }}
      />

      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-5xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <a href="/" className="text-lg font-heading font-semibold text-foreground-900 cursor-pointer whitespace-nowrap">
            Hipobuy Spreadsheet Hub
          </a>
          <div className="hidden md:flex items-center gap-6">
            <a href="/hipobuy-spreadsheet-guide" className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap">Hipobuy</a>
            <a href="/kakobuy-spreadsheet" className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap">Kakobuy</a>
            <a href="/cssbuy-spreadsheet" className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap">CSSBuy</a>
            <a href="/superbuy-spreadsheet" className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap">Superbuy</a>
            <a href="/oopbuy-spreadsheet" className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap">Oopbuy</a>
            <a href="/" className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
              <i className="ri-arrow-left-line"></i>
              Home
            </a>
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-foreground-900"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/60 animate-[fadeIn_0.2s_ease-out] px-4 py-3 space-y-1">
            <a href="/hipobuy-spreadsheet-guide" className="block px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer" onClick={() => setMobileMenuOpen(false)}>Hipobuy Spreadsheet</a>
            <a href="/kakobuy-spreadsheet" className="block px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer" onClick={() => setMobileMenuOpen(false)}>Kakobuy Spreadsheet</a>
            <a href="/cssbuy-spreadsheet" className="block px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer" onClick={() => setMobileMenuOpen(false)}>CSSBuy Spreadsheet</a>
            <a href="/superbuy-spreadsheet" className="block px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer" onClick={() => setMobileMenuOpen(false)}>Superbuy Spreadsheet</a>
            <a href="/oopbuy-spreadsheet" className="block px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer" onClick={() => setMobileMenuOpen(false)}>Oopbuy Spreadsheet</a>
            <div className="border-t border-background-200/60 pt-2 mt-2">
              <a href="/" className="block px-3 py-2.5 text-sm font-medium text-primary-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer" onClick={() => setMobileMenuOpen(false)}>
                <i className="ri-arrow-left-line mr-1"></i> Back to Home
              </a>
            </div>
          </div>
        )}
      </nav>

      <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <header className="mb-10">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="px-2.5 py-1 text-xs font-medium text-primary-700 bg-primary-100 rounded-full whitespace-nowrap">
              {tag}
            </span>
            <span className="text-xs text-foreground-400">{article.publishedDate}</span>
            <span className="text-xs text-foreground-400">&middot;</span>
            <span className="text-xs text-foreground-400">{article.author}</span>
          </div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground-950 leading-tight">
            {article.title}
          </h1>
          <p className="mt-4 text-sm md:text-base text-foreground-500 leading-relaxed max-w-2xl">
            {article.metaDescription}
          </p>
        </header>

        <div className="relative rounded-lg overflow-hidden h-56 md:h-80 mb-12">
          <img
            src={heroImage}
            alt={heroImageAlt}
            title={heroImageTitle}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
        </div>

        <div className="prose-custom space-y-10">
          {article.sections.map((section, idx) => {
            if (section.isFAQ) {
              return (
                <section key={idx} className="bg-background-100 rounded-lg p-6 md:p-8">
                  <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-6">
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-5">
                    {section.faqs?.map((faq, fi) => (
                      <div key={fi} className="border-b border-background-200/70 pb-4 last:border-0 last:pb-0">
                        <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-2">{faq.q}</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    ))}
                  </div>
                </section>
              );
            }
            return (
              <section key={idx}>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-4">
                  {section.heading}
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{section.content}</p>
              </section>
            );
          })}
        </div>

        <section className="mt-14 p-6 md:p-8 bg-background-100 rounded-lg">
          <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-950 mb-4">
            Explore Spreadsheet Categories
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="px-3 py-2 text-xs text-foreground-600 hover:text-foreground-950 hover:bg-background-200/60 rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                {cat.name}
              </a>
            ))}
          </div>
        </section>

        <section className="mt-10 border-t border-background-200/60 pt-8">
          <div className="space-y-6">
            <div>
              <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Related Articles</p>
              <div className="flex flex-wrap gap-2">
                {relatedArticles.map((ra) => (
                  <a
                    key={ra.slug}
                    href={`/${ra.slug}`}
                    className="px-3 py-1.5 text-xs font-medium text-foreground-700 bg-background-100 hover:bg-background-200/60 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {ra.title}
                  </a>
                ))}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4">
              <div>
                <p className="text-xs text-foreground-400 mb-1">Return to Resource Hub</p>
                <a
                  href="/"
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer flex items-center gap-1"
                >
                  <i className="ri-arrow-left-line text-sm"></i>
                  Hipobuy Spreadsheet Hub Homepage
                </a>
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href={siteInfo.mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-4 py-1.5 text-xs font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-all cursor-pointer whitespace-nowrap"
                >
                  hipobuy sheet
                </a>
                <a
                  href={siteInfo.mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-4 py-1.5 text-xs font-semibold text-background-50 bg-foreground-900 hover:bg-foreground-800 rounded-full transition-all cursor-pointer whitespace-nowrap"
                >
                  buy it now
                </a>
              </div>
            </div>
          </div>
        </section>
      </article>

      <footer className="border-t border-background-200/60 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-foreground-500">
            &copy; 2026 Hipobuy Spreadsheet Hub. All rights reserved.
          </p>
          <a
            href="/"
            className="text-xs text-foreground-500 hover:text-foreground-900 transition-colors cursor-pointer"
          >
            Homepage
          </a>
        </div>
      </footer>
    </div>
  );
}